import * as gateway from 'sfx-web-contract';
import { AlkLogger } from '@alk/core';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import { decodeUserSettings, removeUndefinedSettings } from './persistence-adapter.service.utils';
import { DeepRequired } from '../../../types';
import { filter, finalize, map, take, takeUntil, tap } from 'rxjs/operators';
import { inject, injectable } from 'inversify';
import { ISettings, IStreamData, SETTINGS_KEYS } from '../../../interfaces';
import { ISettingsPartialRecord } from '.';
import { Observable } from 'rxjs';
import { PersistenceAdapterAbstractService } from './persistence-adapter.abstract.service';
import { pluckTyped } from '../../../helpers';
import { SerializedRecord } from '../../../interfaces/functions.interface';

@injectable()
export class PersistenceAdapterService extends PersistenceAdapterAbstractService {

    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;

    protected serviceName = 'PersistenceAdapterService';

    protected getInitialSettingsObsInternal = (): Observable<ISettingsPartialRecord> => {
        const request: DeepRequired<gateway.IUserSettingsRequest> = {
            Keys: [ ...SETTINGS_KEYS ],
        };

        const message = gateway.UserSettingsRequest.encode(
            gateway.UserSettingsRequest.create(request),
        ).finish();

        const requestId = this.communicationService.create(
            message,
            gateway.MessageTypes.UserSettings,
        );

        return this.getInitialFeed().pipe(
            filter(response => response.communicationToken === requestId),
            finalize(() => this.communicationService.delete(requestId)),
            pluckTyped('payload'),
            map(decodeUserSettings),
            tap((response) =>
                AlkLogger.info(`[${ this.serviceName }#getInitialSettingsObsInternal] for settings ${ ''
                }${ [ ...SETTINGS_KEYS ] } is ${ JSON.stringify(response) }`)),
            take(1),
        );
    };

    protected saveInternal = (changedSettings: Partial<SerializedRecord<ISettings>>) : Promise<void> => {
        const request: DeepRequired<gateway.IUserSettingsSaveRequest> = {
            Items: removeUndefinedSettings(changedSettings),
        };

        const message = gateway.UserSettingsSaveRequest.encode(
            gateway.UserSettingsSaveRequest.create(request),
        ).finish();

        const requestId = this.communicationService.create(message, gateway.MessageTypes.UserSettingsSave);

        return this.saveFeed().pipe(
            filter(response => response.communicationToken === requestId),
            finalize(() => this.communicationService.delete(requestId)),
            tap((response) =>
                AlkLogger.info(`[${ this.serviceName }#performSave] save ${ ''
                }${ JSON.stringify(request) } is ${ JSON.stringify(response) }`)),
            map(() => undefined),
            take(1),
        ).toPromise();
    };

    private readonly saveFeed = (): Observable<IStreamData<Uint8Array>> =>
        this.communicationService.dataObservable(gateway.MessageTypes.UserSettingsSave)
            .pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
            );

    private readonly getInitialFeed = (): Observable<IStreamData<Uint8Array>> =>
        this.communicationService.dataObservable(gateway.MessageTypes.UserSettings)
            .pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
            );
}
